<template>
  <form @submit.prevent="submitReset">
    <FormGroup>
      <Text variant="h1">Set your new password</Text>

      <View direction="column" gap="sm">
        <FormField label="Password" :error="form.fieldErrors('newPassword')">
          <Input
            v-model="form.fields.newPassword"
            name="password"
            type="password"
            placeholder="Create a new password"
            @focus="showPasswordStrength = true"
          />
        </FormField>

        <PasswordStrength
          :show-component="showPasswordStrength"
          :password="form.fields.newPassword"
          @password-status-change="setPasswordValidity"
        />
      </View>

      <FormField label="Confirm password" :error="form.fieldErrors('confirmPassword')">
        <Input
          v-model="form.fields.confirmPassword"
          name="confirmPassword"
          type="password"
          placeholder="Confirm a new password"
        />
      </FormField>

      <Button type="submit" :loading="form.submitting">Set new password</Button>

      <Text variant="h4" align="center">
        <Link color="accent" :to="backToSignInRoute">Back to Log in</Link>
      </Text>
    </FormGroup>
  </form>
</template>

<script setup lang="ts">
import { helpers, required, sameAs } from '@vuelidate/validators';
import { AxiosError } from 'axios';
import { computed, reactive, ref, watch } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import {
  Button,
  FormField,
  FormGroup,
  Input,
  Link,
  PasswordStrength,
  Text,
  View,
  readableServerMessage,
  serverErrorCode,
  useAlertsStore,
  useForm,
} from '@tallo/design-system';
import { UserRole } from '@tallo/user';

import { useAuthStore } from '../auth.store';
import { seatRemovedErrorCode } from '../model/auth-error-codes';

const props = defineProps<{
  backToSignInRoute: RouteLocationRaw;
  afterLoginRoute: RouteLocationRaw;
  forRole: UserRole;
}>();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const token = route.query.token as string;
const alertsStore = useAlertsStore();
const showPasswordStrength = ref(false);
const passwordValid = ref(false);
const newPassword = computed(() => fields.newPassword);
const fields = reactive({
  newPassword: '',
  confirmPassword: '',
});
const form = useForm<{ newPassword: string; confirmPassword: string }>(fields, {
  newPassword: {
    required,
    strong: () => passwordValid.value,
  },
  confirmPassword: {
    sameAsRaw: helpers.withMessage('Password does not match', sameAs(newPassword)),
  },
});

function setPasswordValidity(valid: boolean) {
  passwordValid.value = valid;
}

async function submitReset() {
  try {
    await form.submit(async () => {
      try {
        await authStore.confirmPasswordReset({
          token,
          forRole: props.forRole,
          newPassword: form.fields.newPassword,
        });

        alertsStore.success('Your password has been reset');

        // Resume the link the guard bounced to sign-in from, if any; a 2FA-gated account is
        // redirected to the code screen by the router guard instead.
        router.push(authStore.consumePostSignInRedirect() ?? props.afterLoginRoute);
      } catch (error) {
        let message = 'Something went wrong. Please try again later';

        if (error instanceof AxiosError) {
          const website = props.forRole === UserRole.INVESTOR ? 'app.tallo.ai' : 'homebase.tallo.ai';

          switch (error.response?.status) {
            case 403:
              // The password is changed before the seat is checked: a removed member keeps it for rejoining
              message =
                serverErrorCode(error) === seatRemovedErrorCode
                  ? (readableServerMessage(error) ??
                    'Your seat on the team was removed. Ask an admin to invite you again.')
                  : 'Your account is not active. Try to sign up again';
              break;

            case 409:
              message = `Reset password at "${website}/auth/reset-password" to continue.`;
              break;
          }
        }

        form.setFieldError('newPassword', message);

        throw error;
      }
    });
  } catch {
    // The form shows why it failed: its own field errors, or the refusal set on the password field
  }
}

watch(form.fields, () => form.clearValidationErrors());
</script>

<style scoped lang="scss">
.back-link-container {
  text-align: center;
}
</style>
